/**
 * **어느 조건이 어느 원장에 걸리나** — 제목 옆 ⓘ 툴팁(`HubInfoTip`)에 싣는 기준 안내 줄.
 *
 * ```
 *   축      소셜 글  광고   상품     근거
 *   제품군    ○      ○     ○      같은 사전(`canonicalProductCategory`)을 세 원장이 쓴다
 *   국가      ○      ○     △      광고는 `adCountry` · 상품은 **판매몰이 곧 나라**다(아마존만 해외)
 *   채널      ○      ✕     ✕      광고는 메타 하나뿐 · 상품의 판매몰은 다른 축이다
 *   기간      ○      ✕     ✕      광고는 집행 구간, 상품은 스냅샷이라 뜻이 다르다
 *   페르소나  ○      ✕     ✕      일반·광고/협찬·브랜드 계정은 글의 판정이다 — 광고·상품·캠페인 원장에는 없다
 *
 *   △ 국가 = 한국·미국만 걸린다. 판매몰이 없는 나라(일본·베트남…)를 고르면 걸 축이 없어
 *       그때만 「국가 필터 미적용」을 적는다 — 판정은 `brand-hub-product-region` 이 한다.
 * ```
 * ⚠️ 제품군은 **`nested` 로 물어야 한다**(`canonicalProductCategory.category.keyword`) — 일반
 *   `term`·`exists` 로 물으면 **몰을 가리지 않고 0** 이 나온다.
 *
 * 🚨 **거짓말을 안 하는 것이 이 파일의 일이다.** 못 거는 조건은 못 건다고 적는다(지금 걸린 것만).
 *   조건을 새로 걸게 되면 **거는 코드와 이 표를 같이** 고쳐야 한다.
 * ⚠️ 분류가 안 붙은 광고·상품은 제품군으로 좁히면 빠진다 — 그 사실도 적는다(「제품군 분류된 …만」).
 */

import type { AdScopeFunnel } from 'src/types/ads-meta/ads-meta-ops';

import { fNumber } from 'src/utils/format/format-number';

import type { BrandHubProductRegion } from './brand-hub-product-region';

/** 위 필터 줄에 걸려 있는 조건 — 값이 있으면 「걸렸다」 */
export type BrandHubScopeOn = {
  category: boolean;
  channels: boolean;
  countries: boolean;
  period: boolean;
  /** 페르소나가 걸렸나 — 안 주면 안 걸린 것으로 본다 */
  persona?: boolean;
};

/** 화면이 쓰는 축 이름 — 필터 줄의 이름 그대로다 */
type Axis = '제품군' | '채널' | '국가' | '기간' | '페르소나';

/**
 * 이 면이 **못 거는** 축 중 **지금 걸려 있는** 것만 한 줄로 — 「기간·채널 필터 미적용」.
 * @param miss 이 면이 못 거는 축 — 적는 차례도 이 차례다
 * @returns 걸린 것이 없으면 빈 배열(고르지도 않은 조건을 적지 않는다)
 */
export function brandHubMissInfo(on: BrandHubScopeOn, miss: readonly Axis[]): string[] {
  const live = onAxes(on);
  const hit = miss.filter((axis) => live.includes(axis));
  return hit.length > 0 ? [`${hit.join('·')} 필터 미적용`] : [];
}

/** 광고 원장의 ⓘ 줄 — 요약 카드 · 개요 · 광고 면이 같이 쓴다. 기간·채널·페르소나는 광고에 안 걸린다 */
export function brandHubAdInfo(on: BrandHubScopeOn): string[] {
  return [
    '메타 광고 기준',
    ...(on.category ? ['제품군 분류된 광고만'] : []),
    ...brandHubMissInfo(on, ['기간', '채널', '페르소나']),
  ];
}

/**
 * 상품 원장의 ⓘ 줄 — 요약 카드 · 개요 · 상품 면이 같이 쓴다.
 *
 * 🚨 상품은 `canonicalBrand` 하나로 거른다 — **브랜드 판정이 안 붙은 상품은 통째로 빠진다**.
 *   적지 않으면 「이 브랜드는 상품이 이것뿐」으로 읽힌다. 「제품군 분류된 것만」과는 다른 말이다.
 * 🔑 해외몰(아마존)은 판정이 더 많이 빠진다 — 해외만 볼 때는 그 말을 붙인다.
 * ⛔ 부착률 숫자(%)를 적지 마라 — 움직이는 값이다.
 * @param region `brandHubProductRegion` 의 값 — 국가가 상품에 걸렸나 · 해외몰만인가
 */
export function brandHubProductInfo(on: BrandHubScopeOn, region: BrandHubProductRegion): string[] {
  return [
    '판매몰 랭킹·검색으로 찾은 상품',
    region.region === 'overseas' ? '브랜드 판정된 상품만 — 해외몰은 넷 중 하나꼴로 누락' : '브랜드 판정된 상품만',
    ...(on.category ? ['제품군 분류된 상품만'] : []),
    ...brandHubMissInfo(on, region.applies ? ['기간', '채널', '페르소나'] : ['기간', '채널', '국가', '페르소나']),
  ];
}

/**
 * 상품 면이 **비었을 때 적는 말**.
 *
 * ⛔ **「해외에는 제품군 분류가 없다」고 적지 마라** — 한때 참이었고(아마존 부착률 0.8%)
 *   지금은 아니다. 상류가 영문 상품명을 읽어 **한국어 제품군**을 고르게 해서 93.8% 가 붙는다
 *   (사전은 안 건드렸다 — 사전 목록 안에서만 고르는 «닫힌 선택»이라 어휘가 안 늘어난다).
 *   그러니 국가와 제품군을 함께 걸어도 이제 정상으로 걸린다.
 * ⚠️ 분류가 비어 있다고 **「아직 안 돌렸다」로 읽지 마라** — `categorySource` 가 있으면
 *   **돌렸는데 «없음»**이다(비뷰티·빈 용기·도구). 그 둘을 가르려면 그 필드를 봐야 한다.
 */
export function brandHubProductEmptyNote(): string {
  return '이 조건에 맞는 상품이 없습니다.';
}

/**
 * 광고 면이 **비었을 때 적는 말** — 「어느 칸에서 0 이 됐나」.
 *
 * 🚨 **0 을 「이 브랜드는 광고를 안 한다」로 적지 마라.** 떨어뜨리는 칸은 대개 제품군
 *   판정이고, 그건 「광고가 없다」가 아니라 **「판정이 안 붙었다」**다. 이 둘을 섞어 적어
 *   하루를 태운 적이 있다 — 그래서 화면이 앞 칸의 수를 같이 적는다.
 * ⚠️ 「집행 중」은 **색인이 그렇게 표시한 것**이다 — 마지막 확인이 낡았을 수 있어
 *   「지금 돌고 있는 광고 N건」으로 단정하지 않는다.
 * @param funnel 조건을 하나씩 얹어 가며 센 수 — 없으면 밋밋한 한 줄로 떨어진다
 */
export function brandHubAdEmptyNote(funnel?: AdScopeFunnel): string {
  if (!funnel || funnel.brand === 0) return '이 조건에 맞는 광고가 없습니다.';
  if (funnel.country === 0) return `이 브랜드 광고 ${funnel.brand.toLocaleString()}건은 모두 다른 나라 집행입니다.`;
  if (funnel.active === 0) return `집행 중인 것이 없습니다 — 끝난 광고는 ${funnel.country.toLocaleString()}건 있습니다.`;
  if (funnel.beauty === 0) return `뷰티 광고로 판정된 것이 없습니다 — 그 앞까지는 ${funnel.active.toLocaleString()}건입니다.`;
  if (funnel.preview === 0) return `미리보기 사진이 없어 못 보여 드립니다 — 광고 자체는 ${funnel.beauty.toLocaleString()}건 있습니다.`;
  if (funnel.category === 0) return `제품군 판정이 안 붙어 0건입니다 — 제품군을 빼면 ${funnel.preview.toLocaleString()}건 보입니다.`;
  return '이 조건에 맞는 광고가 없습니다.';
}

/**
 * 캠페인 면이 **비었을 때 적는 말** — ⛔ 「캠페인이 없습니다」로 적지 마라.
 *
 * 상류는 **브랜드가 낸 글**(공식·광고·협찬)이 근거에 있어야 캠페인으로 친다 — 언급이 많아도 0건이 나온다.
 * @param state 조회가 실패했나(`failed`) · 고른 나라를 상류가 안 덮어 건너뛰었나(`skipped`)
 * @param markets 상류가 덮는 시장 — 비면 「일부 시장」
 */
export function brandHubEventEmptyNote(state: { failed: boolean; skipped: boolean }, markets: readonly string[]): string {
  if (state.failed) return '캠페인을 불러오지 못했습니다.';
  if (state.skipped) {
    const where = markets.length > 0 ? markets.join('·') : '일부 시장';
    return `캠페인은 ${where} 만 모으고 있습니다 — 위 국가 필터에서 그 나라를 고르거나 전체로 비우면 보입니다.`;
  }
  return '확인된 캠페인이 없습니다 — 언급은 있어도 브랜드가 낸 글이 근거에 없으면 세지 않습니다.';
}

/**
 * 상품 면의 「어떻게 담긴 것인가」 한 마디 — 랭킹에 든 것과 **몰에 물어 받아 온 것**을 갈라 적는다.
 *
 * 🚨 갈라 적지 않으면 카드에 「N위」가 없는 줄이 왜 섞여 있는지 화면이 설명하지 못한다.
 * 🚨 **둘을 더하면 총수보다 클 수 있다** — 한 상품이 랭킹에도 오르고 검색으로도 잡히면 양쪽에 센다.
 *   그래서 둘이 함께 설 때는 「(겹칠 수 있음)」을 붙인다.
 * @param kinds 갈래별 수(`ranking` · `new` · `search`) — 없으면 빈 문자열
 */
export function brandHubProductKindNote(kinds: Partial<Record<string, number>> | undefined): string {
  const box = kinds ?? {};
  const ranked = (box.ranking ?? 0) + (box.new ?? 0);
  const searched = box.search ?? 0;
  const parts = [
    ranked > 0 ? `랭킹 진입 ${fNumber(ranked)}` : '',
    searched > 0 ? `검색 확인 ${fNumber(searched)}` : '',
  ].filter(Boolean);
  if (parts.length === 2) return `${parts.join(' · ')} (겹칠 수 있음)`;
  return parts.join(' · ');
}

function onAxes(on: BrandHubScopeOn): Axis[] {
  return [
    on.category ? '제품군' : null,
    on.channels ? '채널' : null,
    on.countries ? '국가' : null,
    on.period ? '기간' : null,
    on.persona ? '페르소나' : null,
  ].filter((axis): axis is Axis => axis != null);
}
